import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Text } from '@notino/react-styleguide';
import { masterUrl } from '@notino/shared/client-utils';
import { GetProductsByIdBatchedQuery } from '@notino/shared/definitions/types';
import {
  IProductCardItemInput,
  ProductCard,
  ProductCardModel,
  StockAvailability,
  useMapProductsToCardProps,
} from '@notino/web-libraries-product-card';

import { useSettings } from '@containers/ProductDetailContainer/hooks/useSettings';
import { useIsDualPrice } from '@containers/ProductDetailContainer/ProductDetail/hooks/useIsDualPrice';
import { messages as glamourDayMessages } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/GlamourDay/messages';
import { isDiorBrand } from '@containers/ProductDetailContainer/ProductDetail/utils';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { ProductEventWither } from '@helpers/googleTagManager';
import { ATTRIBUTION_TOKEN_QUERY_PARAM } from '@utils/constants';
import { noop } from '@utils/noop';
import { appendQueryParam } from '@utils/url';

import { useProductSpaRedirect } from '../../ClientRedirect/useHandleClientRedirect';
import { usePriceConfig } from '../../PriceLabel/usePriceConfig';
import { useWishlistActionsFactory } from '../WishlistModifier/useWishlistActions';
import { useWishlistLists } from '../WishlistModifier/useWishlistLists';
import { useWishlistSelectionDrawer } from '../WishlistModifier/useWishlistSelectionDrawer';

import { toCardProduct } from './cardProduct';
import { BuyButton } from './components';
import { messages } from './messages';
import { IProductItemProps } from './model';

type UseGetProductItemProps = Pick<
  IProductItemProps,
  | 'useMasterUrl'
  | 'setProductWishlisted'
  | 'GTMEventLabel'
  | 'sendGTM'
  | 'noPriceIfUnavailable'
  | 'showWishlistButton'
  | 'onProductAdded'
  | 'onProductAddFailed'
  | 'onClosingCartModal'
  | 'showBuyButton'
  | 'showAddToCartModal'
  | 'buttonElementClassName'
  | 'canSpaRedirect'
  | 'wishlistActionLocation'
  | 'hasRegisteredWatchdog'
  | 'listIdForWishlistOperations'
>;

type GetProductProps = Pick<
  IProductItemProps,
  | 'product'
  | 'order'
  | 'appendComponent'
  | 'description'
  | 'position'
  | 'attributionToken'
  | 'shouldHideItemBuyButton'
  | 'onClickOveride'
  | 'buyButtonDisabled'
  | 'pictogramsHidden'
  | 'isSponsored'
  | 'selectable'
  | 'selected'
>;

type ProductItem = NonNullable<
  GetProductsByIdBatchedQuery['vpProductByIds'][number]
>;

/**
 * One product ready for `useProductCards`: the package mapper input plus the
 * card props the package mapper does not know about.
 */
export interface IProductCardItem {
  input: IProductCardItemInput;
  /** Shown above the discount label. */
  notice?: React.ReactNode;
  /** Shown below the discount label: appended content and the buy button. */
  additionalContent?: React.ReactNode;
  description?: string;
  order?: number;
  selectable?: boolean;
  selected?: boolean;
  anchorProps?: ProductCardModel.IProductCardContentProps['anchorProps'];
}

const resolveProductHref = ({
  url,
  useMasterUrl,
  attributionToken,
}: {
  url: ProductItem['url'];
  useMasterUrl: boolean;
  attributionToken?: string;
}) => {
  const resolvedUrl = useMasterUrl ? masterUrl(url ?? '') : url ?? '';
  return attributionToken
    ? appendQueryParam(
        resolvedUrl,
        ATTRIBUTION_TOKEN_QUERY_PARAM,
        attributionToken
      )
    : resolvedUrl;
};

export const useGetProductItemProps = ({
  useMasterUrl = false,
  setProductWishlisted,
  GTMEventLabel,
  sendGTM = false,
  noPriceIfUnavailable,
  showWishlistButton = true,
  onProductAdded = noop,
  onProductAddFailed = noop,
  onClosingCartModal = noop,
  showBuyButton = false,
  showAddToCartModal = true,
  buttonElementClassName,
  canSpaRedirect = true,
  wishlistActionLocation,
  hasRegisteredWatchdog,
  listIdForWishlistOperations,
}: UseGetProductItemProps) => {
  const { data: { currentLocale, currentCurrency } = {} } = usePriceConfig();
  const { Settings: { showUnitPrices } = {} } = useSettings();
  const { fetchLists, lastFetchedListsRef } = useWishlistLists();
  const { formatMessage } = useIntl();
  const { getSecondaryPrice } = useIsDualPrice();

  const withProductSpaRedirect = useProductSpaRedirect(
    canSpaRedirect,
    useMasterUrl
  );

  const { handleWishlistClick } = useWishlistActionsFactory({
    onCompleted: (products) =>
      products.forEach((p) =>
        setProductWishlisted?.(p.productId, p.wishlisted)
      ),
  });

  const { open: openListSelection } = useWishlistSelectionDrawer({
    onSave: async ({
      addedToLists,
      removedFromLists,
      productIds,
      listPosition,
    }) => {
      await handleWishlistClick({
        productIds,
        actionLocation: wishlistActionLocation!,
        listName: GTMEventLabel,
        listPosition,
        lists: lastFetchedListsRef.current ?? [],
        addToLists: addedToLists,
        removeFromLists: removedFromLists,
      });
    },
  });

  return ({
    product,
    order,
    appendComponent,
    description,
    position = 0,
    attributionToken,
    shouldHideItemBuyButton,
    onClickOveride,
    buyButtonDisabled,
    pictogramsHidden,
    isSponsored,
    selectable,
    selected,
  }: GetProductProps): IProductCardItem | null => {
    if (!currentLocale || !currentCurrency || !product) {
      return null;
    }

    const handleTracking = () => {
      if (sendGTM && currentCurrency) {
        dispatchTrackingEvent({
          event: 'select_item',
          product: [
            ProductEventWither()
              .withVpProduct(
                product as Parameters<
                  ReturnType<typeof ProductEventWither>['withVpProduct']
                >[0]
              )
              .withDiscountPrices(product.attributes, {
                shouldSendOnlyDiscountPrice: true,
              })
              .withAdditionalData({
                list_position: position,
                list_name: GTMEventLabel,
              })
              .build(),
          ],
          _clear: true,
        });
      }
    };

    const href = resolveProductHref({
      url: product.url,
      useMasterUrl,
      attributionToken,
    });

    const withSpaRedirect = withProductSpaRedirect({
      ...(product as { id: string; catalogMasterId: number }),
      url: href,
    });

    const cardProduct = toCardProduct(product, {
      url: href,
      annotation: useMasterUrl ? product.annotation : product.fullAnnotation,
      showPrice: !noPriceIfUnavailable || product.canBuy,
      showUnitPrice: Boolean(showUnitPrices),
      hideLabels: isDiorBrand(product.brand?.id),
      hideBadges: pictogramsHidden,
      isSponsored: Boolean(isSponsored),
      toSecondaryPrice: getSecondaryPrice ?? undefined,
      glamourDayMessage: formatMessage(glamourDayMessages.title),
    });

    const isOutOfStock =
      cardProduct.stockAvailability === StockAvailability.OUT_OF_STOCK;

    return {
      input: {
        product: cardProduct,
        tileProps: {
          'data-cypress': 'productItem-container',
        },
        onClick: onClickOveride ?? withSpaRedirect(handleTracking),
        wishlist: showWishlistButton
          ? {
              wishlisted: product.wishlisted ?? false,
              onClick: async () => {
                const lists = await fetchLists();
                if (lists.length === 1 || listIdForWishlistOperations) {
                  await handleWishlistClick({
                    productIds: [product.id!],
                    actionLocation: wishlistActionLocation!,
                    listName: GTMEventLabel,
                    listPosition: position,
                    notifyFailed: true,
                    addToLists: product.wishlisted
                      ? []
                      : [
                          {
                            id:
                              listIdForWishlistOperations ||
                              useWishlistLists.DEFAULT_LIST_ID,
                          },
                        ],
                    removeFromLists: product.wishlisted
                      ? [
                          {
                            id:
                              listIdForWishlistOperations ||
                              useWishlistLists.DEFAULT_LIST_ID,
                          },
                        ]
                      : [],
                    lists,
                  });
                  return;
                }
                openListSelection({
                  lists,
                  productId: product.id!,
                  productName: `${product.brand?.name ?? ''} ${
                    product.name ?? ''
                  }`,
                });
              },
            }
          : undefined,
      },
      // The card renders an out-of-stock product itself; a product in stock can
      // still be unbuyable.
      notice: !product.canBuy && !isOutOfStock && (
        <Text
          typography="labelSmall400"
          color="text.negative"
          marginTop="0.5rem"
        >
          <FormattedMessage
            {...(product.showWatchdog
              ? messages.notAvailableText
              : messages.disabledProductText)}
          />
        </Text>
      ),
      additionalContent: (
        <>
          {appendComponent}
          {showBuyButton && !shouldHideItemBuyButton && (
            <BuyButton
              disabled={buyButtonDisabled}
              onProductAddFailed={onProductAddFailed}
              onProductAdded={onProductAdded}
              buttonElementClassName={buttonElementClassName!}
              product={product}
              showAddToCartModal={showAddToCartModal}
              disableRedirect={true}
              onClosingCartModal={onClosingCartModal}
              hasRegisteredWatchdog={hasRegisteredWatchdog}
            />
          )}
        </>
      ),
      description,
      order,
      selectable,
      selected,
    };
  };
};

/**
 * Maps products through the package mapper. The package renders its discount
 * label after `aboveBuyButton`; ours sits above the buy button, so it moves into
 * that slot between the notice and the buy button.
 */
export const useProductCards = (
  items: Array<IProductCardItem | null | undefined>
): ProductCardModel.IProductCardContentProps[] => {
  const presentItems = items.filter((item): item is IProductCardItem =>
    Boolean(item)
  );
  const cards = useMapProductsToCardProps(
    presentItems.map(({ input }) => input)
  );
  const Discount = ProductCard.Discount;

  return cards.map(({ discountLabel, ...card }, index) => {
    const {
      notice,
      additionalContent,
      description,
      order,
      selectable,
      selected,
      anchorProps,
    } = presentItems[index];

    return {
      ...card,
      product: { ...card.product, description },
      order,
      selectable,
      selected,
      anchorProps,
      aboveBuyButton: (
        <>
          {notice}
          {discountLabel && Discount && <Discount {...discountLabel} />}
          {additionalContent}
        </>
      ),
    };
  });
};

export const useProductItemProps = ({
  product,
  description,
  position,
  appendComponent,
  order,
  onClickOveride,
  shouldHideItemBuyButton,
  buyButtonDisabled,
  pictogramsHidden,
  selectable,
  selected,
  ...rest
}: IProductItemProps) => {
  const getProductItemProps = useGetProductItemProps(rest);
  const [productProps] = useProductCards([
    getProductItemProps({
      product,
      description,
      position,
      appendComponent,
      order,
      onClickOveride,
      shouldHideItemBuyButton,
      buyButtonDisabled,
      pictogramsHidden,
      selectable,
      selected,
    }),
  ]);

  return productProps ?? null;
};
